/**
 * Leads Service — React Query hooks.
 *
 * Reads go through `directApiClient` (LF-3016). Cache invalidation is direct
 * rather than via `lib/services/query-dependencies.ts`: that module is
 * matter-only (`getBatchDependencies` types `matterId` as required) and a lead
 * has no matter until it converts. Scribe and Intake set the same precedent —
 * both invalidate through their own `…Keys.all`.
 */

import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';
import { useCallback } from 'react';
import { leadKeys } from '@/lib/query-keys/leads';
import { contactKeys } from '@/lib/query-keys/contacts';
import { FRESH_DATA_CONFIG } from '@/lib/services/query-config';
import type { RefreshFromWordResult } from '@/lib/services/templates';
import {
  addLeadContact,
  removeLeadContact,
  updateLeadContact,
  captureLead,
  clearLeadOutstandingItem,
  closeLead,
  convertLead,
  createLeadCostAgreement,
  createLeadMeeting,
  deleteLead,
  deleteLeadDocument,
  markLeadDocumentsSeen,
  deleteLeadMeeting,
  editLeadCostAgreementInWord,
  fetchLead,
  fetchLeadBoard,
  fetchLeadCostAgreementPrecedents,
  fetchLeadDocuments,
  fetchLeadDuplicates,
  fetchLeadMeetingCalendarAvailability,
  fetchLeadOriginForMatter,
  hasSearchableDuplicateTerm,
  isTerminalBoard,
  linkLeadMatter,
  markLeadCostAgreementSigned,
  overrideLeadStage,
  preRenderLeadEmail,
  recordLeadOutstandingItem,
  refreshLeadCostAgreementFromWord,
  reopenLead,
  sendLeadCostAgreement,
  sendLeadMeetingBrief,
  setLeadAllocation,
  setLeadFlag,
  setupLead,
  updateLead,
  updateLeadMeeting
} from './client';
import type {
  AddLeadContactInput,
  LeadContact,
  UpdateLeadContactInput,
  CaptureLeadInput,
  CloseLeadInput,
  ConvertLeadInput,
  CreateLeadCostAgreementInput,
  CreateLeadMeetingInput,
  CreateLeadOutstandingItemInput,
  EditLeadCostAgreementInWordResponse,
  LeadBoardFilters,
  LeadBoardPage,
  LeadBoardResponse,
  LeadConvertResult,
  LeadCostAgreementPrecedent,
  LeadDetail,
  LeadDocument,
  LeadDuplicatesResponse,
  LeadMeetingBriefResponse,
  LeadMeetingCalendarAvailability,
  LeadMeetingWriteResponse,
  LeadOrigin,
  LinkLeadMatterInput,
  OverrideLeadStageInput,
  PreRenderedLeadEmail,
  PreRenderLeadEmailInput,
  SendLeadCostAgreementInput,
  SetupLeadInput,
  SetupLeadResponse,
  UpdateLeadInput,
  UpdateLeadMeetingInput
} from './types';

/**
 * How many closed leads a terminal tab shows at a time, and how many each
 * `Show 25 more` appends (`functional-spec.md` §3.7).
 *
 * Matches the backend's `LEAD_BOARD_DEFAULT_PAGE_SIZE`. It is a page size, not
 * a ceiling — the backend caps `limit` at 100, which is why the tab accumulates
 * *pages* below rather than growing a single request's limit.
 */
export const LEAD_BOARD_PAGE_SIZE = 25;

/**
 * `GET /leads` — the board read.
 *
 * Uses `FRESH_DATA_CONFIG` (30s stale, refetch on focus) because the board is
 * a shared worklist: a coordinator watches it while colleagues send forms and
 * clients reply, so a stale pipeline is the failure mode that matters here.
 *
 * `placeholderData: keepPreviousData` keeps the previous tab's rows on screen
 * while a new filter loads, so changing a filter does not blank the board.
 *
 * **Not self-gating on the `leads` feature flag.** The route owns that gate and
 * simply does not mount this surface for a firm without the flag (the
 * `contacts-page` precedent), which keeps the data layer free of a provider
 * dependency. Pass `enabled: false` for any caller that needs to hold the
 * fetch back — mounting it for an unflagged firm would 404 by design.
 */
export function useLeadBoard(
  filters?: LeadBoardFilters,
  options?: { enabled?: boolean }
) {
  return useQuery<LeadBoardResponse>({
    queryKey: leadKeys.board(filters),
    queryFn: () => fetchLeadBoard(filters),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
    ...FRESH_DATA_CONFIG
  });
}

/**
 * `GET /leads?outcome=proceeding|not_proceeding` — a **terminal tab**, page by
 * page (LF-5985).
 *
 * The In-progress board is a pipeline and arrives whole; a terminal tab is a
 * log that outgrows it within weeks, so §3.7 caps it at 25 with `Show 25 more`.
 * That is modelled as an infinite query rather than a growing `limit` for two
 * reasons: the backend bounds `limit` at 100, so raising it would hit a wall
 * after the third click; and re-requesting the whole accumulated list on every
 * click would re-transfer every row already on screen.
 *
 * `outcome` must be a terminal value. A response that comes back sectioned
 * means the caller passed `open`, which is `useLeadBoard`'s job — it throws
 * rather than returning an empty page, so the mistake surfaces as an error
 * instead of as a tab that is silently always empty.
 */
export function useLeadBoardTerminal(
  filters: Omit<LeadBoardFilters, 'page' | 'limit'>,
  options?: { enabled?: boolean }
) {
  return useInfiniteQuery<LeadBoardPage>({
    queryKey: leadKeys.terminalPages(filters),
    queryFn: async ({ pageParam }) => {
      const response = await fetchLeadBoard({
        ...filters,
        page: pageParam as number,
        limit: LEAD_BOARD_PAGE_SIZE
      });
      if (!isTerminalBoard(response)) {
        throw new Error(
          'Expected a paginated terminal board; received the sectioned In-progress board.'
        );
      }
      return response.page;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.currentPage < lastPage.totalPages
        ? lastPage.currentPage + 1
        : undefined,
    enabled: options?.enabled ?? true,
    ...FRESH_DATA_CONFIG
  });
}

/**
 * Invalidate every lead query.
 *
 * Single-sourced here so the mutation surfaces that follow (capture, inline
 * edits, flag, close, convert) do not each hand-write the key — the drift that
 * `query-dependencies.ts` exists to prevent for matters, applied to leads
 * without wiring a lead into a matter-shaped map.
 */
export function useInvalidateLeads() {
  const queryClient = useQueryClient();
  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: leadKeys.all }),
    [queryClient]
  );
}

/**
 * `POST /leads` — capture a new enquiry (LF-5987 / LEADS-20,
 * `functional-spec.md` §5.1).
 *
 * On success the whole lead cache is invalidated, which is what makes the new
 * enquiry land at the **top of `New enquiry`**: the board's ordering is
 * server-side, so the refetch places it rather than any client-side splice.
 * The returned detail is also seeded into `leadKeys.detail(id)`, so the surface
 * that opens the lead has it without a second round trip — `POST /leads`
 * already returns the full payload precisely so that read can be skipped.
 *
 * Deliberately **not** optimistic. An optimistic row would have to invent every
 * derived fact the board renders (section, effective stage, day counts), which
 * is the one thing this module forbids the frontend from doing — and a capture
 * is a single fast write the coordinator is watching, not a latency-sensitive
 * toggle.
 */
export function useCaptureLead() {
  const queryClient = useQueryClient();

  return useMutation<LeadDetail, Error, CaptureLeadInput>({
    mutationFn: (input) => captureLead(input),
    onSuccess: (lead) => {
      queryClient.setQueryData(leadKeys.detail(lead.id), lead);
      void queryClient.invalidateQueries({ queryKey: leadKeys.all });
    }
  });
}

/**
 * `GET /leads/:id` — one lead's detail, for the drawer and the lead page
 * (LF-5988 / LEADS-21, `functional-spec.md` §4).
 *
 * **`enabled` is driven by `leadId`, which is what makes the drawer cheap.** The
 * drawer stays mounted across leads and holds `null` when closed, so passing
 * `null` parks the query rather than unmounting the surface — and the two hosts
 * (drawer and page) share one hook, one key and one cache entry, so opening a
 * lead the board just listed, or one the capture modal just created, resolves
 * from cache with no visible fetch.
 *
 * `FRESH_DATA_CONFIG` for the same reason as the board: a lead is a shared
 * record, and a colleague sending the intake form changes what this drawer
 * should say.
 */
export function useLead(leadId: number | null) {
  return useQuery<LeadDetail>({
    // Non-null asserted only inside the `enabled` guard's shadow — React Query
    // never calls `queryFn` while `enabled` is false.
    queryKey: leadKeys.detail(leadId ?? 0),
    queryFn: () => fetchLead(leadId as number),
    enabled: leadId != null,
    ...FRESH_DATA_CONFIG,
    /*
      A meeting's Outlook event is written after the booking commits (CAL-3), so
      the response that saved it still says `pending`. Re-read while any write is
      in flight, so the Meeting card's Calendar row settles on its own instead of
      reading "Adding to Outlook" until someone reloads.
    */
    refetchInterval: (query) =>
      query.state.data?.meetings.some(
        (meeting) => meeting.calendarState === 'pending'
      )
        ? LEAD_CALENDAR_PENDING_POLL_MS
        : false
  });
}

/** How often a lead with an Outlook write in flight is re-read. */
const LEAD_CALENDAR_PENDING_POLL_MS = 4000;

/**
 * `PATCH /leads/:id` — one inline field edit (LF-5988 / LEADS-21,
 * `functional-spec.md` §4.3).
 *
 * **Optimistic on the detail, invalidating on the board.** The patched fields are
 * written into `leadKeys.detail(id)` before the request goes out, so the card the
 * coordinator is typing in reflects the edit with no round-trip flicker; the
 * server's full response then replaces that entry, because an edit can move a
 * *derived* value the frontend must not compute (correcting `enquiryAt` changes
 * `daysInStage`), and merging a partial response would leave the derived block
 * describing the pre-edit lead.
 *
 * The **board** is invalidated rather than patched, for the rule this whole module
 * is built on: a row carries section membership, effective stage, stall and
 * cost-agreement standing, all resolved by `LeadStageService`. Splicing an edited
 * value into a cached row would make the frontend the second author of those
 * facts. So the row underneath updates by refetching — which is also what keeps
 * its *ordering* the server's, since an edit can legitimately move a row.
 *
 * **A rejected save rolls the cache back and rethrows.** The caller — the field
 * grid — is what keeps the field open with the typing intact (§4.3): only it
 * knows what was being typed, and a hook that swallowed the error would leave the
 * coordinator believing a 400 had saved.
 */
/**
 * The three composed names, re-derived from their parts (VALE-1505).
 *
 * The `name` rows read `clientName` / `client2Name` / `deceasedName`, strings
 * the server composes, and write only the parts, so a shallow merge of the
 * patch left the composed key stale and the row repainted the OLD name for the
 * whole round trip after a surname was corrected: the request-shape versus
 * record-shape mismatch `components/attributes/CLAUDE.md` §0 records, on an
 * edit surface. Mirrors `formatPersonName` and `formatLeadEnquirerNames` in the
 * backend's `lead-name.utils.ts` (trimmed parts joined by one space, `null`
 * when none; both enquirers joined by ` & `) rather than inventing a rule.
 */
const JOINT_ENQUIRER_SEPARATOR = ' & ';
function composePersonName(
  ...parts: Array<string | null | undefined>
): string | null {
  const composed = parts
    .map((part) => part?.trim())
    .filter((part): part is string => !!part && part.length > 0)
    .join(' ');
  return composed.length > 0 ? composed : null;
}
export function deriveComposedNames(
  lead: LeadDetail,
  patch: UpdateLeadInput
): LeadDetail {
  const touched = (keys: Array<keyof UpdateLeadInput>) =>
    keys.some((key) => key in patch);
  const next = { ...lead };
  const clientTouched = touched([
    'clientFirstName',
    'clientMiddleName',
    'clientLastName'
  ]);
  const client2Touched = touched([
    'client2FirstName',
    'client2MiddleName',
    'client2LastName'
  ]);
  if (clientTouched) {
    next.clientName = composePersonName(
      next.clientFirstName,
      next.clientMiddleName,
      next.clientLastName
    );
  }
  if (client2Touched) {
    next.client2Name = composePersonName(
      next.client2FirstName,
      next.client2MiddleName,
      next.client2LastName
    );
  }
  if (clientTouched || client2Touched) {
    next.clientNames = [next.clientName, next.client2Name]
      .filter((name): name is string => name !== null)
      .join(JOINT_ENQUIRER_SEPARATOR);
  }
  if (
    touched(['deceasedFirstName', 'deceasedMiddleName', 'deceasedLastName'])
  ) {
    next.deceasedName = composePersonName(
      next.deceasedFirstName,
      next.deceasedMiddleName,
      next.deceasedLastName
    );
  }
  return next;
}

export function useUpdateLead() {
  const queryClient = useQueryClient();

  return useMutation<
    LeadDetail,
    Error,
    { leadId: number; patch: UpdateLeadInput },
    { previous: LeadDetail | undefined }
  >({
    mutationFn: ({ leadId, patch }) => updateLead(leadId, patch),
    onMutate: async ({ leadId, patch }) => {
      // Stop an in-flight read landing on top of the optimistic write — without
      // this, a `FRESH_DATA_CONFIG` refetch that started before the keystroke can
      // resolve after it and visibly undo the edit.
      await queryClient.cancelQueries({ queryKey: leadKeys.detail(leadId) });

      const previous = queryClient.getQueryData<LeadDetail>(
        leadKeys.detail(leadId)
      );

      if (previous) {
        // A shallow merge is exactly right: `UpdateLeadInput` is a flat set of
        // scalar columns plus `address`, which is replaced wholesale rather than
        // merged (clearing a suburb must not silently keep the old one). The
        // one exception is the server-composed names, derived below.
        queryClient.setQueryData<LeadDetail>(
          leadKeys.detail(leadId),
          deriveComposedNames({ ...previous, ...patch }, patch)
        );
      }

      return { previous };
    },
    onError: (_error, { leadId }, context) => {
      // Put back exactly what was there. Restoring by refetch instead would race
      // the field's own retry and could flash the failed value back on screen.
      if (context?.previous) {
        queryClient.setQueryData(leadKeys.detail(leadId), context.previous);
      }
    },
    onSuccess: (lead) => {
      queryClient.setQueryData(leadKeys.detail(lead.id), lead);
      // Boards only — `leadKeys.all` would also invalidate the detail entry just
      // written and spend a request re-reading what the response already gave.
      void queryClient.invalidateQueries({ queryKey: leadKeys.boards() });
    }
  });
}

/**
 * `GET /leads/duplicates` — duplicate awareness while the coordinator types
 * (R1.6).
 *
 * **This warns and never blocks.** It is deliberately decoupled from the
 * capture: an error here resolves to no candidates rather than surfacing, so a
 * failing search can never stop an enquiry being saved. `retry: false` for the
 * same reason — a keystroke-scoped read is superseded by the next one, so
 * retrying a stale question only spends requests.
 *
 * The caller passes the **debounced** terms. The query stays disabled until one
 * of them clears the backend's minimum length, so the modal fires no request at
 * all for the first two characters of a name.
 */
export function useLeadDuplicates(params: {
  clientNames?: string;
  deceasedName?: string;
  email?: string;
  phone?: string;
}) {
  const enabled = hasSearchableDuplicateTerm(params);

  return useQuery<LeadDuplicatesResponse>({
    queryKey: leadKeys.duplicates(params),
    queryFn: () => fetchLeadDuplicates(params),
    enabled,
    ...FRESH_DATA_CONFIG,
    // After the spread deliberately — `FRESH_DATA_CONFIG` carries its own
    // `retry`, and this read must override it rather than be overridden.
    retry: false
  });
}

/**
 * The cache write every lead mutation that returns a whole `LeadDetail` shares.
 *
 * **Seed the detail, invalidate the boards.** The response is the complete,
 * server-derived lead, so writing it straight into `leadKeys.detail(id)` saves a
 * round trip the caller would otherwise spend re-reading what it already holds.
 * The **boards** are invalidated rather than patched, for the rule this module
 * is built on: a row carries section membership, effective stage, stall and
 * cost-agreement standing, all resolved by `LeadStageService`. Splicing an
 * updated row into a cached board would make the frontend the second author of
 * those facts — and would get the *ordering* wrong too, since closing a lead
 * moves it into a terminal tab whose position is decided by the closure that
 * mutation just recorded.
 *
 * Single-sourced here rather than repeated across the four mutations below,
 * which is the drift `useInvalidateLeads` exists to prevent one level up.
 */
function useLeadDetailResultHandler() {
  const queryClient = useQueryClient();

  return useCallback(
    (lead: LeadDetail) => {
      queryClient.setQueryData(leadKeys.detail(lead.id), lead);
      void queryClient.invalidateQueries({ queryKey: leadKeys.boards() });
    },
    [queryClient]
  );
}

/**
 * `POST /leads/:id/close` — the Status card's two outcome decisions (LF-5996 /
 * LEADS-29, `functional-spec.md` §5.7, §4.7).
 *
 * One hook for both `Mark proceeding` and `Not proceeding…`, because they are
 * one endpoint: both are closures, and both must record the `closed` event the
 * two terminal tabs are ordered by.
 *
 * **Deliberately not optimistic.** Closing changes which *tab* the lead lives
 * in and where it sits in that tab's ordering — derived facts the frontend is
 * forbidden from computing. An optimistic close would have to invent them, and
 * a rejected one (a missing reason, an already-closed lead) would then have to
 * un-invent them. It is a single deliberate write behind a confirmation, not a
 * latency-sensitive toggle.
 *
 * **Errors are rethrown** so the close dialog can stay open with the reason and
 * note intact — the same contract as the inline-edit hook, and for the same
 * reason: only the caller knows what was being entered.
 */
export function useCloseLead() {
  const onLeadUpdated = useLeadDetailResultHandler();

  return useMutation<
    LeadDetail,
    Error,
    { leadId: number; input: CloseLeadInput }
  >({
    mutationFn: ({ leadId, input }) => closeLead(leadId, input),
    onSuccess: onLeadUpdated
  });
}

/** `POST /leads/:id/reopen` — return a closed enquiry to the board (R9.4). */
export function useReopenLead() {
  const onLeadUpdated = useLeadDetailResultHandler();

  return useMutation<LeadDetail, Error, number>({
    mutationFn: (leadId) => reopenLead(leadId),
    onSuccess: onLeadUpdated
  });
}

/**
 * `DELETE /leads/:id` — delete an enquiry the firm does not want (VALE-1616).
 *
 * **`invalidateLeads()`, not `useLeadDetailResultHandler`.** The other writes
 * here seed the detail cache with the reconciled lead and invalidate the boards;
 * this one has no lead to seed, and it has to reach further than the boards. A
 * deleted enquiry leaves the in-progress sections, both terminal tabs *and their
 * `totalItems`* (which drives the `Show N more` count, so a stale one offers a
 * phantom row), the quick-actions search — which reads `leadKeys.board({ q })` —
 * the duplicate warning at capture, and the matter origin band. `leadKeys.all`
 * is the one key that covers every one of them.
 *
 * **Errors are rethrown** so `DeleteConfirmationDialog` keeps its own state and
 * renders the failure in place — the `useCloseLead` contract, for the same
 * reason: only the dialog knows what the user was in the middle of.
 */
export function useDeleteLead() {
  const invalidateLeads = useInvalidateLeads();

  return useMutation<void, Error, number>({
    mutationFn: (leadId) => deleteLead(leadId),
    onSuccess: () => void invalidateLeads()
  });
}

/**
 * `POST /leads/:id/stage` — the Status card's manual override (LF-6022 /
 * LEADS-51, `functional-spec.md` §5.6).
 *
 * **Deliberately not optimistic**, for the strongest version of the reason the
 * close hook gives: a stage move changes the enquiry's board *section*, resets its
 * `daysInStage`, re-ranks it inside that section, and can flip `effectiveStage`
 * onto the derived `ready_to_book` rung. Those are exactly the facts
 * `LeadStageService` owns and the frontend is forbidden from computing, so an
 * optimistic write would have to invent all four and a rejected one un-invent
 * them.
 *
 * **Errors are rethrown** so the menu's call site can surface them in the card,
 * the same contract as the outstanding-item writes beside it — the menu closes on
 * click either way, so a rejected override is otherwise indistinguishable from a
 * successful one.
 */
export function useOverrideLeadStage() {
  const onLeadUpdated = useLeadDetailResultHandler();

  return useMutation<
    LeadDetail,
    Error,
    { leadId: number; input: OverrideLeadStageInput }
  >({
    mutationFn: ({ leadId, input }) => overrideLeadStage(leadId, input),
    onSuccess: onLeadUpdated
  });
}

/**
 * `POST /leads/:id/flag` · `DELETE` — set or clear the board's manual mark
 * (`functional-spec.md` §3.5.9).
 *
 * **Takes the value it wants, never a toggle.** The caller already renders the
 * current state, so it passes the opposite; the hook does not read the cache to
 * work one out. That keeps a double-click idempotent instead of racing itself
 * back to where it started.
 *
 * **Deliberately not optimistic, unlike the inline field edit.** The response is
 * the whole re-derived lead and the row does not move (§2.11 — a flag never
 * reorders), so the only thing an optimistic write would buy is the amber wash
 * arriving one round trip sooner — at the cost of a rollback path for a call
 * that can 403 or 404. The shared result handler seeds the detail and
 * invalidates the boards, which is what puts the wash on every surface showing
 * this lead rather than only the one that was clicked.
 */
export function useToggleLeadFlag() {
  const onLeadUpdated = useLeadDetailResultHandler();

  return useMutation<LeadDetail, Error, { leadId: number; flagged: boolean }>({
    mutationFn: ({ leadId, flagged }) => setLeadFlag(leadId, flagged),
    onSuccess: onLeadUpdated
  });
}

/**
 * `POST /leads/:id/allocation` · `DELETE` — hand the enquiry over for
 * allocation, or take it back.
 *
 * **Takes the value it wants, never a toggle**, for the same reason the flag
 * does and with more riding on it: the hand-over emails the firm's allocator, so
 * a click that inverted whatever the server currently held could tell somebody
 * to pick an enquiry up and then silently remove it from their queue.
 *
 * **Not optimistic.** The hand-over moves the enquiry between board sections, so
 * an optimistic write would have to re-derive membership in the browser — the
 * one thing this module forbids — and it can legitimately fail (`400
 * LEAD_ALLOCATION_NOT_CONFIGURED`, `409 LEAD_ALREADY_CLOSED`). The shared result
 * handler seeds the detail and invalidates the boards, which is what moves the
 * row out of its old section on every surface showing this enquiry.
 */
export function useSetLeadAllocation() {
  const onLeadUpdated = useLeadDetailResultHandler();

  return useMutation<LeadDetail, Error, { leadId: number; requested: boolean }>(
    {
      mutationFn: ({ leadId, requested }) =>
        setLeadAllocation(leadId, requested),
      onSuccess: onLeadUpdated
    }
  );
}

/**
 * `POST /leads/:id/outstanding` — record an advisory item (§2.5).
 *
 * The board is invalidated along with the detail because the row renders the
 * longest-standing open item; nothing about the lead's *section* or ordering
 * changes, since an outstanding item never gates a stage.
 */
export function useRecordLeadOutstandingItem() {
  const onLeadUpdated = useLeadDetailResultHandler();

  return useMutation<
    LeadDetail,
    Error,
    { leadId: number; input: CreateLeadOutstandingItemInput }
  >({
    mutationFn: ({ leadId, input }) => recordLeadOutstandingItem(leadId, input),
    onSuccess: onLeadUpdated
  });
}

/** `DELETE /leads/:id/outstanding/:itemId` — clear an item (the row is kept, stamped cleared). */
export function useClearLeadOutstandingItem() {
  const onLeadUpdated = useLeadDetailResultHandler();

  return useMutation<LeadDetail, Error, { leadId: number; itemId: number }>({
    mutationFn: ({ leadId, itemId }) =>
      clearLeadOutstandingItem(leadId, itemId),
    onSuccess: onLeadUpdated
  });
}

/**
 * The enquiry's contact writes (VALE-2061). Each returns the one row, not the lead,
 * so the lead is re-read: the contact list and the intake form's unlisted executors
 * both come off the detail payload, and the server owns the matching between
 * them. Contacts are refreshed too, since an add can mint one and an edit changes
 * one everywhere it appears.
 *
 * Errors are rethrown: the drawer's commit path surfaces a failure by the
 * promise rejecting.
 */
function useLeadContactResultHandler() {
  const queryClient = useQueryClient();
  return useCallback(
    (leadId: number) => {
      void queryClient.invalidateQueries({ queryKey: leadKeys.detail(leadId) });
      void queryClient.invalidateQueries({ queryKey: contactKeys.all });
    },
    [queryClient]
  );
}

export function useAddLeadContact() {
  const onChanged = useLeadContactResultHandler();
  return useMutation<
    LeadContact,
    Error,
    { leadId: number; input: AddLeadContactInput }
  >({
    mutationFn: ({ leadId, input }) => addLeadContact(leadId, input),
    onSuccess: (_lpr, { leadId }) => onChanged(leadId)
  });
}

export function useUpdateLeadContact() {
  const onChanged = useLeadContactResultHandler();
  return useMutation<
    LeadContact,
    Error,
    { leadId: number; leadContactId: number; input: UpdateLeadContactInput }
  >({
    mutationFn: ({ leadId, leadContactId, input }) =>
      updateLeadContact(leadId, leadContactId, input),
    onSuccess: (_lpr, { leadId }) => onChanged(leadId)
  });
}

export function useRemoveLeadContact() {
  const onChanged = useLeadContactResultHandler();
  return useMutation<void, Error, { leadId: number; leadContactId: number }>({
    mutationFn: ({ leadId, leadContactId }) =>
      removeLeadContact(leadId, leadContactId),
    onSuccess: (_result, { leadId }) => onChanged(leadId)
  });
}

/**
 * `POST /leads/:id/setup` — the setup dialog's one write (LF-5994 / LEADS-27,
 * `functional-spec.md` §5.2).
 *
 * Deliberately **not** optimistic, and not merely because a send is slow: the
 * call dispatches real email, and the artefact it records is what moves the
 * stage — a fact only the server settles, and only forward. There is nothing
 * honest to show ahead of the response.
 *
 * Shares `useLeadDetailResultHandler` with the outcome mutations above: every
 * one of these returns the whole re-read lead precisely so no follow-up read is
 * needed, and there must be one definition of what to do with it.
 */
export function useSetupLead() {
  const onLeadUpdated = useLeadDetailResultHandler();

  return useMutation<
    SetupLeadResponse,
    Error,
    { leadId: number; input: SetupLeadInput }
  >({
    mutationFn: ({ leadId, input }) => setupLead(leadId, input),
    onSuccess: (response) => onLeadUpdated(response.lead)
  });
}

/**
 * `POST /leads/:id/emails/pre-render` — the review step's rendered preview of
 * the booking confirmation (LEADS-46, §5.2).
 *
 * **A read, despite the POST**: the body carries the unsaved meeting, and the
 * server writes nothing — so it is a `useQuery`, cached on the input, not a
 * mutation. Pass `input: null` to hold the request (the review step is not open,
 * or the summary still shows a "Still needed" row the send would refuse).
 *
 * **`retry: false` is load-bearing.** The expected failure here is a deliberate
 * `400` — the same three refusals the send makes — which is a stable answer, not
 * a transient fault. Retrying it three times would delay the dialog for a verdict
 * that cannot change, and the summary above the preview has already named the
 * missing field.
 */
export function useLeadEmailPreview(
  leadId: number | null,
  input: PreRenderLeadEmailInput | null
) {
  return useQuery<PreRenderedLeadEmail>({
    queryKey: leadKeys.emailPreview(leadId ?? 0, input),
    queryFn: () =>
      preRenderLeadEmail(leadId as number, input as PreRenderLeadEmailInput),
    enabled: leadId != null && input != null,
    ...FRESH_DATA_CONFIG,
    // AFTER the spread: `FRESH_DATA_CONFIG` sets its own `retry`, so ordering
    // this first silently discards the override (caught by TS2783).
    retry: false
  });
}

/**
 * Would a booking made now go out as an Outlook calendar invitation (CAL-3)?
 * Enabled only where a meeting is being **booked**: sync is decided at booking
 * and an edit carries no toggle, so nothing else needs the answer.
 *
 * Keyed on the selected solicitor (their mailbox is the organiser chain's second
 * rung). `placeholderData` keeps the previous answer while a new solicitor's
 * loads, so the switch does not flicker out and back on every pick.
 */
export function useLeadMeetingCalendarAvailability(
  leadId: number | null,
  bookedSolicitorId: number | null,
  enabled: boolean
) {
  return useQuery<LeadMeetingCalendarAvailability>({
    queryKey: leadKeys.meetingCalendarAvailability(
      leadId ?? 0,
      bookedSolicitorId
    ),
    queryFn: () =>
      fetchLeadMeetingCalendarAvailability(leadId as number, bookedSolicitorId),
    enabled: enabled && leadId != null,
    placeholderData: keepPreviousData,
    ...FRESH_DATA_CONFIG
  });
}

/** `POST /leads/:id/meetings` — record an appointment on its own (§5.2). */
export function useCreateLeadMeeting() {
  const onLeadUpdated = useLeadDetailResultHandler();

  return useMutation<
    LeadMeetingWriteResponse,
    Error,
    { leadId: number; input: CreateLeadMeetingInput }
  >({
    mutationFn: ({ leadId, input }) => createLeadMeeting(leadId, input),
    onSuccess: (response) => onLeadUpdated(response.lead)
  });
}

/** `PATCH /leads/:id/meetings/:meetingId` — §5.3's narrower edit dialog. */
export function useUpdateLeadMeeting() {
  const onLeadUpdated = useLeadDetailResultHandler();

  return useMutation<
    LeadMeetingWriteResponse,
    Error,
    { leadId: number; meetingId: number; patch: UpdateLeadMeetingInput }
  >({
    mutationFn: ({ leadId, meetingId, patch }) =>
      updateLeadMeeting(leadId, meetingId, patch),
    onSuccess: (response) => onLeadUpdated(response.lead)
  });
}

/**
 * `POST /leads/:id/meetings/:meetingId/brief` — push the pre-meeting brief now
 * (§6.2 phase 2, the on-demand half).
 *
 * The response carries the refreshed lead, so the Meeting card's `Brief sent`
 * line and the Activity timeline's new entry both land from this one call — no
 * follow-up `GET /leads/:id`.
 */
export function useSendLeadMeetingBrief() {
  const onLeadUpdated = useLeadDetailResultHandler();

  return useMutation<
    LeadMeetingBriefResponse,
    Error,
    { leadId: number; meetingId: number }
  >({
    mutationFn: ({ leadId, meetingId }) =>
      sendLeadMeetingBrief(leadId, meetingId),
    onSuccess: (response) => onLeadUpdated(response.lead)
  });
}

/** `DELETE /leads/:id/meetings/:meetingId` — the appointment did not happen. */
export function useDeleteLeadMeeting() {
  const onLeadUpdated = useLeadDetailResultHandler();

  return useMutation<LeadDetail, Error, { leadId: number; meetingId: number }>({
    mutationFn: ({ leadId, meetingId }) => deleteLeadMeeting(leadId, meetingId),
    onSuccess: onLeadUpdated
  });
}

/**
 * `POST /leads/:id/convert` — mint a matter from this enquiry (R9.1, LF-6021 /
 * LEADS-50, `functional-spec.md` §5.8).
 *
 * **Not `useLeadDetailResultHandler`.** The response is a `LeadConvertResult`
 * — the matter id/reference/link-source and the per-artefact bind report —
 * not a whole `LeadDetail`, so there is nothing to seed into
 * `leadKeys.detail(id)` directly. Invalidate instead: the caller (the header's
 * convert dialog) reads the mutation's own returned data for the immediate
 * bind-report confirmation, and the refetched lead is what then carries the
 * new `matterId` / `matterReference` onto the header chip and the board row.
 *
 * Deliberately **not optimistic** — a matter-creating call with a per-artefact
 * bind outcome and a possible `409 LEAD_MATTER_DIVERGENCE` has nothing honest
 * to show ahead of the response.
 */
export function useConvertLead() {
  const invalidateLeads = useInvalidateLeads();

  return useMutation<
    LeadConvertResult,
    Error,
    { leadId: number; input?: ConvertLeadInput }
  >({
    mutationFn: ({ leadId, input }) => convertLead(leadId, input),
    onSuccess: () => void invalidateLeads()
  });
}

/**
 * `POST /leads/:id/link-matter` — associate an existing matter (R9.5,
 * LF-6021 / LEADS-50).
 *
 * Same cache-write shape as {@link useConvertLead} and for the same reason:
 * the response is a `LeadConvertResult`, not a `LeadDetail`.
 */
export function useLinkLeadMatter() {
  const invalidateLeads = useInvalidateLeads();

  return useMutation<
    LeadConvertResult,
    Error,
    { leadId: number; input: LinkLeadMatterInput }
  >({
    mutationFn: ({ leadId, input }) => linkLeadMatter(leadId, input),
    onSuccess: () => void invalidateLeads()
  });
}

// =============================================================================
// The cost agreement — LF-6012 / LEADS-45, `functional-spec.md` §4.10 / §5.5
// =============================================================================

/**
 * `GET /leads/:id/cost-agreement/precedents` — the Configure step's picker.
 *
 * `enabled` is driven by `leadId`, so a closed dialog costs nothing: the two
 * hosts keep the card mounted and the dialog is what asks. An empty list is a
 * real answer (the firm has authored no lead-ready precedent), not an error, so
 * callers branch on `data?.length`, never on `isError`.
 */
export function useLeadCostAgreementPrecedents(leadId: number | null) {
  return useQuery<LeadCostAgreementPrecedent[]>({
    queryKey: leadKeys.costAgreementPrecedents(leadId ?? 0),
    queryFn: () => fetchLeadCostAgreementPrecedents(leadId as number),
    enabled: leadId != null,
    ...FRESH_DATA_CONFIG
  });
}

/**
 * `POST /leads/:id/cost-agreement` — draft through either of §4.10's two doors.
 *
 * Deliberately **not** optimistic: the call renders a real document through
 * Gotenberg and can legitimately refuse (a missing `<<signatureBlock>>`, a
 * precedent asking for data the enquiry does not hold), so there is nothing
 * honest to show ahead of the response. **Errors are rethrown** so the dialog
 * stays open on its Configure step with the chosen precedent and the attached
 * file intact — the same contract the setup dialog and the inline-edit grid use.
 */
export function useCreateLeadCostAgreement() {
  const onLeadUpdated = useLeadDetailResultHandler();

  return useMutation<
    LeadDetail,
    Error,
    { leadId: number; input: CreateLeadCostAgreementInput }
  >({
    mutationFn: ({ leadId, input }) => createLeadCostAgreement(leadId, input),
    onSuccess: onLeadUpdated
  });
}

/**
 * `POST /leads/:id/cost-agreement/send` — create the envelope and email the
 * signing link.
 *
 * **`retry: false` is load-bearing, not a preference.** Each Annature envelope
 * is billed and `createEnvelope` has no retry of its own, so an automatic retry
 * of a request that may have already reached the vendor would charge the firm
 * twice and leave two envelopes racing to write one `signedAt`. React Query's
 * default retry is exactly that hazard, so it is switched off here rather than
 * relied upon to be harmless.
 */
export function useSendLeadCostAgreement() {
  const onLeadUpdated = useLeadDetailResultHandler();

  return useMutation<
    LeadDetail,
    Error,
    { leadId: number; input?: SendLeadCostAgreementInput }
  >({
    mutationFn: ({ leadId, input }) => sendLeadCostAgreement(leadId, input),
    retry: false,
    onSuccess: onLeadUpdated
  });
}

/**
 * `POST /leads/:id/cost-agreement/mark-signed` — record a signature obtained
 * outside Vale (§4.10's fallback).
 */
export function useMarkLeadCostAgreementSigned() {
  const onLeadUpdated = useLeadDetailResultHandler();

  return useMutation<LeadDetail, Error, number>({
    mutationFn: (leadId) => markLeadCostAgreementSigned(leadId),
    onSuccess: onLeadUpdated
  });
}

/**
 * `POST /leads/:id/cost-agreement/edit-in-word` — open the drafted agreement in
 * Word (LF-6159).
 *
 * **`retry: false`, for a different reason than the send's.** Provisioning is
 * idempotent, so a retry is safe in the money sense — but it is slow (a
 * SharePoint round trip), and the caller has already opened a placeholder tab
 * that is sitting blank while it waits. A silent retry doubles that wait with
 * no signal, so a failure surfaces immediately and the user clicks again.
 *
 * The lead detail is **not** invalidated on success: nothing about the
 * agreement has changed yet — the edit lands later, through the read seam.
 */
export function useEditLeadCostAgreementInWord() {
  return useMutation<EditLeadCostAgreementInWordResponse, Error, number>({
    mutationFn: (leadId) => editLeadCostAgreementInWord(leadId),
    retry: false
  });
}

/**
 * `POST /leads/:id/cost-agreement/refresh-from-word` — "I'm back from Word"
 * (VALE-288).
 *
 * Runs the read seam, then refetches the lead so the card's provenance line and
 * its document link catch up with the pulled bytes. The lead detail read runs no
 * seam of its own, so without this the card shows whatever S3 held until the
 * ~30s change notification lands.
 *
 * Silent in itself, because its other consumer is the window-focus effect and
 * that fires on nothing the user asked for. It resolves to the outcome so the
 * *manual* control can say when Word could not be reached, which is the case
 * that otherwise looks identical to "checked, nothing new" (VALE-258). Use
 * `mutateAsync` and read `status` where that matters.
 *
 * `retry: false` for the same reason as the edit above: the seam is an
 * accelerator, and a silent retry only lengthens the wait behind a spinner.
 */
export function useRefreshLeadCostAgreementFromWord() {
  const queryClient = useQueryClient();

  return useMutation<RefreshFromWordResult, Error, number>({
    mutationFn: (leadId) => refreshLeadCostAgreementFromWord(leadId),
    retry: false,
    onSuccess: async (_result, leadId) => {
      await queryClient.invalidateQueries({
        queryKey: leadKeys.detail(leadId)
      });
    }
  });
}

/**
 * `GET /leads/by-matter/:matterId` — the matter overview's "origin lead" chip
 * (LF-6021 / LEADS-50). `null` is the normal case for the vast majority of
 * matters, which were never a lead — so this is cheap to mount unconditionally
 * on the matter header.
 *
 * `staleTime: Infinity`-adjacent: a matter's origin lead, if any, cannot
 * change after the fact (there is no "un-link" for this direction), so the
 * default `FRESH_DATA_CONFIG` refetch-on-focus this module uses elsewhere
 * would just re-ask a settled question. Cached indefinitely per matter id.
 */
export function useLeadOriginForMatter(matterId: number) {
  return useQuery<LeadOrigin | null>({
    queryKey: leadKeys.originForMatter(matterId),
    queryFn: () => fetchLeadOriginForMatter(matterId),
    staleTime: Infinity
  });
}

// =============================================================================
// Client-supplied documents — LF-6029 / LEADS-53
// =============================================================================

/**
 * `GET /leads/:id/documents` — the enquiry's scanned originals (§4.2's Documents
 * card).
 *
 * `null` parks the read, exactly as `useLead` does, so a closed drawer costs
 * nothing. Its own query key rather than a slice of the lead detail: uploading or
 * removing a document changes nothing else about the lead, and folding it into
 * `detail()` would bounce every inline field and the whole rail on each dropped
 * file.
 */
export function useLeadDocuments(leadId: number | null) {
  return useQuery<LeadDocument[]>({
    queryKey: leadKeys.documents(leadId ?? 0),
    queryFn: () => fetchLeadDocuments(leadId as number),
    enabled: leadId != null,
    ...FRESH_DATA_CONFIG
  });
}

/**
 * Refetch one enquiry's document list, and its detail.
 *
 * A hook in the data layer rather than a `useQueryClient()` call inside the card,
 * for two reasons. Cache invalidation is the service layer's job here — the same
 * rule that keeps `leadKeys` out of components — and a component that reaches for
 * `useQueryClient()` itself hard-requires a `QueryClientProvider` from **every**
 * host that mounts it, including the several lead suites that deliberately mock
 * `@/lib/services/leads` precisely so they need no provider. Exposing it here keeps
 * the card mockable exactly like its siblings.
 *
 * Also invalidates `leadKeys.detail(leadId)` (VALE-1453): a scanned death
 * certificate settles `deathCertificateIssued`, the outstanding item and the
 * activity entry server-side, all on the detail response — a documents-only
 * invalidation left the drawer showing the pre-recognition lead until reload.
 */
export function useRefreshLeadDocuments() {
  const queryClient = useQueryClient();

  return useCallback(
    (leadId: number) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: leadKeys.documents(leadId) }),
        queryClient.invalidateQueries({ queryKey: leadKeys.detail(leadId) })
      ]),
    [queryClient]
  );
}

/**
 * `POST /leads/:id/documents/mark-seen` — the Documents card was looked at while
 * the board's "New documents" signal was up.
 *
 * Invalidates the lead detail and the boards on success, since the signal is
 * server-derived on both and the frontend re-derives none of it.
 */
export function useMarkLeadDocumentsSeen() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: (leadId) => markLeadDocumentsSeen(leadId),
    onSuccess: (_result, leadId) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: leadKeys.detail(leadId) }),
        queryClient.invalidateQueries({ queryKey: leadKeys.boards() })
      ])
  });
}

/**
 * `DELETE /leads/:id/documents/:fileId` — remove one document.
 *
 * Invalidates only this lead's document list. **Not optimistic**: the server
 * soft-deletes and there is no undo, so removing the row before the call settles
 * would show a document gone that might still be there. Errors are swallowed into
 * a toast by the card — a failed remove leaves the row, which is the honest state.
 */
export function useDeleteLeadDocument() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { leadId: number; fileId: number }>({
    mutationFn: ({ leadId, fileId }) => deleteLeadDocument(leadId, fileId),
    onSuccess: (_result, { leadId }) =>
      queryClient.invalidateQueries({ queryKey: leadKeys.documents(leadId) })
  });
}
